import { Emitter } from '@ar-suite/core'

export type CameraFacing = 'user' | 'environment'

export interface CameraOptions {
  facing: CameraFacing
  /** Ideal capture size. Defaults to 1280×720. */
  width: number
  height: number
  frameRate: number
  /** Explicit device id (overrides facing). */
  deviceId?: string
  /** Existing <video> to attach to; otherwise one is created. */
  video?: HTMLVideoElement
}

export const CAMERA_DEFAULTS: CameraOptions = { facing: 'user', width: 1280, height: 720, frameRate: 30 }

export interface CameraEvents extends Record<string, unknown> {
  start: { width: number; height: number; facing: CameraFacing }
  stop: undefined
  error: Error
}

export class CameraError extends Error {
  constructor(public readonly code: 'unsupported' | 'denied' | 'notfound' | 'inuse' | 'unknown', message: string, public override readonly cause?: unknown) {
    super(message)
    this.name = 'CameraError'
  }
}

/**
 * Wraps getUserMedia: front/back switching, resolution, device enumeration.
 * The <video> is muted + playsInline so iOS Safari autoplays.
 */
export class CameraManager extends Emitter<CameraEvents> {
  readonly video: HTMLVideoElement
  private stream: MediaStream | null = null
  private _options: CameraOptions
  private _facing: CameraFacing

  constructor(options: Partial<CameraOptions> = {}) {
    super()
    this._options = { ...CAMERA_DEFAULTS, ...options }
    this._facing = this._options.facing
    this.video = this._options.video ?? document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.autoplay = true
    this.video.setAttribute('playsinline', '')
    this.video.setAttribute('muted', '')
  }

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  }

  get facing(): CameraFacing {
    return this._facing
  }
  /** True when the preview should be mirrored (selfie camera). */
  get mirrored(): boolean {
    return this._facing === 'user'
  }
  get running(): boolean {
    return !!this.stream && this.video.readyState >= 2
  }
  get width(): number {
    return this.video.videoWidth
  }
  get height(): number {
    return this.video.videoHeight
  }
  get aspect(): number {
    return this.video.videoWidth / Math.max(1, this.video.videoHeight)
  }
  get mediaStream(): MediaStream | null {
    return this.stream
  }

  async start(overrides: Partial<CameraOptions> = {}): Promise<void> {
    if (!CameraManager.isSupported()) throw new CameraError('unsupported', 'getUserMedia is not available (needs HTTPS or localhost)')
    this._options = { ...this._options, ...overrides }
    this._facing = this._options.facing
    this.stopStream()
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        width: { ideal: this._options.width },
        height: { ideal: this._options.height },
        frameRate: { ideal: this._options.frameRate },
        ...(this._options.deviceId ? { deviceId: { exact: this._options.deviceId } } : { facingMode: this._facing }),
      },
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints)
    } catch (e) {
      const err = this.wrapError(e)
      this.emit('error', err)
      throw err
    }
    const track = this.stream.getVideoTracks()[0]
    const settings = track?.getSettings()
    if (settings?.facingMode === 'environment') this._facing = 'environment'
    else if (settings?.facingMode === 'user') this._facing = 'user'
    this.video.srcObject = this.stream
    await this.video.play().catch(() => undefined)
    if (this.video.readyState < 2) {
      await new Promise<void>((resolve) => {
        const done = () => {
          this.video.removeEventListener('loadeddata', done)
          resolve()
        }
        this.video.addEventListener('loadeddata', done)
      })
    }
    this.emit('start', { width: this.video.videoWidth, height: this.video.videoHeight, facing: this._facing })
  }

  async switchFacing(): Promise<void> {
    await this.start({ facing: this._facing === 'user' ? 'environment' : 'user', deviceId: undefined })
  }

  async listDevices(): Promise<MediaDeviceInfo[]> {
    if (!CameraManager.isSupported()) return []
    const devs = await navigator.mediaDevices.enumerateDevices()
    return devs.filter((d) => d.kind === 'videoinput')
  }

  stop(): void {
    this.stopStream()
    this.emit('stop', undefined)
  }

  private stopStream(): void {
    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop()
      this.stream = null
    }
    this.video.srcObject = null
  }

  private wrapError(e: unknown): CameraError {
    const name = (e as { name?: string })?.name ?? ''
    if (name === 'NotAllowedError' || name === 'SecurityError') return new CameraError('denied', 'Camera permission denied', e)
    if (name === 'NotFoundError' || name === 'OverconstrainedError') return new CameraError('notfound', 'No matching camera found', e)
    if (name === 'NotReadableError' || name === 'AbortError') return new CameraError('inuse', 'Camera is in use by another app', e)
    return new CameraError('unknown', (e as Error)?.message ?? 'Camera error', e)
  }
}
